import { Button, TextInput } from '@mantine/core';
import type { AxiosError } from 'axios';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ApiErrorResponse, FormSummary, Problem, UsageResponse } from '@/api';
import { Currency } from '@/api/types/generated';
import { useAddressPool } from '@/components/FormCreate/createForm';
import { QuickCreate } from '@/components/FormCreate/QuickCreate';
import {
  byLatestMessage,
  FormNameCell,
  LastMessageCell,
  ProblemsCell,
  RecipientsCell,
} from '@/components/FormsList/cells';
import { problemCopy, problemKey } from '@/components/Problems/problemCopy';
import { DataTable } from '@/components/Ui/DataTable';
import { Fact, FactGrid } from '@/components/Ui/Fact';
import { NavCount } from '@/components/Ui/NavCount';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { WarningIcon } from '@/components/Ui/RiskStrip';
import { Section } from '@/components/Ui/Section';
import { Tag } from '@/components/Ui/Tag';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { formatCredit, formatNumber, formatPercent } from '@/lib/money';
import { shortDate } from '@/lib/relative-time';
import { planLabel } from '@/pages/Account/Billing/billing-labels';
import { useRustyState } from '@/state';
import classes from './Home.module.css';

const ATTENTION_ROWS = 8;
const FORM_ROWS = 8;

type Load<T> = { status: 'loading' } | { status: 'error' } | { status: 'ready'; data: T };

export function HomeScreen() {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const forms = useRustyState((s) => s.formsSummary);
  const [formsFailed, setFormsFailed] = useState(false);
  const [problems, setProblems] = useState<Load<Problem[]>>({ status: 'loading' });

  useEffect(() => {
    let ignore = false;
    const { api, refreshFormsSummary, refreshAccountBalance } = useRustyState.getState();
    void refreshAccountBalance();
    void refreshFormsSummary().then((res) => {
      if (!ignore) setFormsFailed(res === null);
    });
    api
      .getProblems({}, { onError: false })
      .then((res) => {
        if (!ignore) setProblems({ status: 'ready', data: res.data ?? [] });
      })
      .catch(() => {
        if (!ignore) setProblems({ status: 'error' });
      });
    return () => {
      ignore = true;
    };
  }, []);

  const isNew = forms !== null && forms.length === 0;

  return (
    <Page>
      <PageHeader
        title={t('home.pageTitle')}
        actions={
          isNew ? undefined : (
            <Button component={Link} to={createLanguageURL('/account/forms/new')} variant="filled">
              {t('home.newForm')}
            </Button>
          )
        }
      />

      <FactGrid>
        <PlanFact />
        <CreditFact />
        {isNew ? <NotificationsFact /> : <MessagesFact forms={forms} />}
      </FactGrid>

      {isNew && <QuickCreate />}

      <NeedsAttention problems={problems} isNew={isNew} />

      {isNew ? (
        <Section title={t('home.formsTitle')}>
          <div className={classes.empty}>{t('home.noForms')}</div>
        </Section>
      ) : (
        <FormsSection forms={forms} failed={formsFailed} />
      )}
    </Page>
  );
}

function PlanFact() {
  const { t, i18n } = useTranslation();
  const subscription = useRustyState((s) => s.currentSubscription);
  const [usage, setUsage] = useState<UsageResponse | null>(null);

  const subscriptionId = subscription?.subscription_id;

  useEffect(() => {
    // Without a subscription the usage endpoint answers 404.
    if (!subscriptionId) return;
    let ignore = false;
    useRustyState
      .getState()
      .api.getSubscriptionUsage()
      .then((rows) => {
        if (!ignore) setUsage(rows.find((r) => r.service === 'message') ?? null);
      })
      .catch(() => undefined);
    return () => {
      ignore = true;
    };
  }, [subscriptionId]);

  const compare = <UiLink to="/account/billing?section=plans">{t('home.comparePlans')}</UiLink>;

  if (!subscription) {
    return <Fact label={t('home.plan')} value={t('home.noPlan')} help={compare} />;
  }

  if (subscription.is_free_plan) {
    return (
      <Fact
        label={t('home.plan')}
        value={planLabel(subscription.plan_name)}
        help={
          <>
            {usage && usage.limit > 0
              ? t('home.planFree', {
                  count: usage.limit,
                  n: formatNumber(usage.limit, i18n.language),
                })
              : t('home.planFreeNoAllowance')}{' '}
            {compare}
          </>
        }
      />
    );
  }

  const percent = usage && usage.limit > 0 ? Math.round((usage.used / usage.limit) * 100) : null;
  const date = subscription.current_period_end
    ? shortDate(subscription.current_period_end, i18n.language.split('-')[0])
    : '';
  return (
    <Fact
      label={t('home.plan')}
      value={planLabel(subscription.plan_name)}
      bar={percent ?? undefined}
      help={
        percent === null
          ? undefined
          : date
            ? t('home.planAllowance', { percent: formatPercent(percent, i18n.language), date })
            : t('home.planAllowanceNoDate', { percent: formatPercent(percent, i18n.language) })
      }
    />
  );
}

function CreditFact() {
  const { t, i18n } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const balance = useRustyState((s) => s.accountBalance);
  const euro = balance?.find(([currency]) => currency === Currency.EUR) ?? [Currency.EUR, 0];
  return (
    <Fact
      label={t('home.credit')}
      value={balance ? formatCredit(euro[1], i18n.language) : '…'}
      help={t('home.creditHelp')}
      action={
        <Button component={Link} to={createLanguageURL('/account/billing')} size="xs">
          {t('home.addCredit')}
        </Button>
      }
    />
  );
}

function MessagesFact({ forms }: { forms: FormSummary[] | null }) {
  const { t, i18n } = useTranslation();
  if (!forms) {
    return <Fact label={t('home.messages7d')} value="…" />;
  }
  const total = forms.reduce((n, f) => n + f.messages_7d, 0);
  const spam = forms.reduce((n, f) => n + f.spam_7d, 0);
  return (
    <Fact
      label={t('home.messages7d')}
      value={formatNumber(total, i18n.language)}
      help={
        <>
          {t('home.messages7dHelp', { count: forms.length })}
          {spam > 0 && (
            <>
              {' '}
              {t('home.messages7dSpam', { count: spam, n: formatNumber(spam, i18n.language) })}{' '}
              <UiLink to="/account/messages?verdict=spam">{t('home.reviewSpam')}</UiLink>
            </>
          )}
        </>
      }
    />
  );
}

function NotificationsFact() {
  const { t } = useTranslation();
  const session = useRustyState((s) => s.getSession());
  // The store's pool, polled while an address is pending, is what the guided start reads too.
  const { pool, loaded } = useAddressPool();
  const emails = loaded ? pool : null;
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const email = address.trim();
    if (!email) return;
    setBusy(true);
    setError(null);
    try {
      await useRustyState
        .getState()
        .api.newVerifiedEmail({ email }, { onSuccess: false, onError: false });
      setSentTo(email);
      setAddress('');
      await useRustyState
        .getState()
        .getAndSetVerifiedEmails()
        .catch(() => undefined);
    } catch (err) {
      const code = (err as AxiosError<ApiErrorResponse>).response?.data?.code;
      setError(
        code === 'email_owned_by_other_account'
          ? t('home.ownedByOther')
          : code === 'resend_limit'
            ? t('addresses.add.resendLimit')
            : t('home.verifyFailed')
      );
    } finally {
      setBusy(false);
    }
  };

  const current =
    emails?.find((e) => e.is_account_email && e.is_verified) ??
    emails?.find((e) => e.is_verified) ??
    emails?.find((e) => !e.is_verified);

  if (emails === null) {
    return <Fact label={t('home.notificationsGoTo')} value="…" />;
  }

  if (current) {
    return (
      <Fact
        label={t('home.notificationsGoTo')}
        value={<span className={classes.address}>{current.email}</span>}
        help={
          current.is_verified ? (
            <Tag variant="ok">{t('home.verified')}</Tag>
          ) : (
            <>
              <Tag variant="pending">{t('home.pending')}</Tag>{' '}
              {sentTo ? t('home.verifySent', { email: sentTo }) : t('home.pendingHelp')}
            </>
          )
        }
      />
    );
  }

  return (
    <Fact
      label={t('home.notificationsGoTo')}
      value={
        <form className={classes.verifyForm} onSubmit={submit}>
          <TextInput
            className={classes.verifyInput}
            type="email"
            required
            aria-label={t('home.addressLabel')}
            placeholder="you@example.com"
            value={address}
            onChange={(e) => setAddress(e.currentTarget.value)}
            error={!!error}
          />
          <Button type="submit" loading={busy}>
            {t('home.verify')}
          </Button>
        </form>
      }
      help={
        <>
          {session?.publicKey && !session.email ? t('home.noAddressNostr') : t('home.noAddress')}
          {error && (
            <span className={classes.error} role="alert">
              {' '}
              {error}
            </span>
          )}
        </>
      }
    />
  );
}

function NeedsAttention({ problems, isNew }: { problems: Load<Problem[]>; isNew: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split('-')[0];

  const rows =
    problems.status === 'ready'
      ? problems.data
          .filter((p) => !p.muted && (p.group === 'action' || p.group === 'waiting'))
          .sort((a, b) => (a.group === b.group ? 0 : a.group === 'action' ? -1 : 1))
      : [];
  const actionCount = rows.filter((p) => p.group === 'action').length;

  return (
    <Section
      title={
        <span className={classes.titleRow}>
          {actionCount > 0 && <WarningIcon className={classes.icon} />}
          {t('home.needsAttention')}
          {actionCount > 0 && <NavCount variant="risk">{actionCount}</NavCount>}
        </span>
      }
      actions={isNew ? undefined : <UiLink to="/account/problems">{t('home.allProblems')}</UiLink>}
    >
      {problems.status === 'loading' && <div className={classes.empty}>{t('ui.loading')}</div>}
      {problems.status === 'error' && (
        <div className={classes.empty}>{t('home.problemsLoadFailed')}</div>
      )}
      {problems.status === 'ready' && rows.length === 0 && (
        <div className={classes.empty}>
          {isNew ? t('home.nothingYet') : t('home.nothingNeedsAttention')}
        </div>
      )}
      {rows.slice(0, ATTENTION_ROWS).map((p) => {
        const copy = problemCopy(t, p, locale);
        const waiting = p.group === 'waiting';
        return (
          <div
            key={problemKey(p)}
            className={waiting ? `${classes.prob} ${classes.waitingRow}` : classes.prob}
          >
            <span className={classes.probText}>
              <span className={classes.small}>
                {p.form_id ? (
                  <UiLink to={`/account/forms/${p.form_id}`}>{p.form_title ?? p.form_id}</UiLink>
                ) : (
                  t('home.account')
                )}
              </span>
              <span>
                {copy.title && <strong>{copy.title}.</strong>} {copy.body}
              </span>
            </span>
            {waiting ? (
              <span className={classes.small}>{t('home.waiting')}</span>
            ) : (
              copy.fix && <UiLink to={copy.fix.to}>{copy.fix.label}</UiLink>
            )}
          </div>
        );
      })}
      {rows.length > ATTENTION_ROWS && (
        <div className={classes.empty}>
          {t('home.attentionShown', { shown: ATTENTION_ROWS, total: rows.length })}{' '}
          <UiLink to="/account/problems">{t('home.allProblems')}</UiLink>
        </div>
      )}
    </Section>
  );
}

function FormsSection({ forms, failed }: { forms: FormSummary[] | null; failed: boolean }) {
  const { t } = useTranslation();
  const rows = forms ? [...forms].sort(byLatestMessage).slice(0, FORM_ROWS) : [];
  return (
    <Section
      title={t('home.formsLatest')}
      actions={
        forms ? (
          <UiLink to="/account/forms">{t('home.allForms', { count: forms.length })}</UiLink>
        ) : undefined
      }
    >
      <DataTable
        caption={t('home.formsLatest')}
        rowKey={(f) => f.id}
        rows={rows}
        empty={failed ? t('home.formsLoadFailed') : t('ui.loading')}
        columns={[
          {
            key: 'form',
            header: t('forms.list.colForm'),
            render: (f) => <FormNameCell form={f} />,
          },
          {
            key: 'n7',
            header: t('forms.list.colLast7'),
            align: 'num',
            render: (f) => f.messages_7d,
          },
          {
            key: 'last',
            header: t('forms.list.colLastMessage'),
            render: (f) => <LastMessageCell form={f} />,
          },
          {
            key: 'recipients',
            header: t('forms.list.colRecipients'),
            render: (f) => <RecipientsCell form={f} />,
          },
          {
            key: 'problems',
            header: t('forms.list.colProblems'),
            render: (f) => <ProblemsCell form={f} />,
          },
        ]}
      />
    </Section>
  );
}
